import { DEFAULT_PAGE_SIZE } from "@/core/constants/pagination";
import type { HttpQueryParam, HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  collectAllPages,
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  toPaginatedQuery,
} from "@/core/utils";
import { appendReportQuery, parseContentDispositionFileName } from "@/modules/export";
import type { ExportedFile } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import {
  DEFAULT_PROFISSIONAIS_SORT,
  INDISPONIVEL_FILTER_NO,
  INDISPONIVEL_FILTER_YES,
} from "./profissionais.types";
import type {
  ExportProfissionaisParams,
  ListProfissionaisParams,
  ListProfissionalFilterOptionsParams,
  Profissional,
  ProfissionalFilterOption,
  ProfissionalIndisponibilidade,
  ProfissionalPeriodo,
  ProfissionalRemoteFilterColumn,
  ProfissionaisPage,
  ProfissionaisPeriodo,
} from "./profissionais.types";

/** Contrato consumido do hub-bff (HU 1761). */
export const PROFISSIONAIS_PATHS = {
  LIST: "/api/recursos/profissionais",
  /**
   * O técnico da HU não lista um recurso de exportação: o caminho segue o
   * mesmo padrão usado por Embarcações/Usuários (`/exportacao`) até o bff
   * confirmar o contrato definitivo — ver pendências no relatório da tarefa.
   */
  EXPORT: "/api/recursos/profissionais/exportacao",
} as const;

/** Opções de um filtro de coluna: um serviço por coluna (Cargo e Time). */
export function buildProfissionalFilterOptionsPath(column: ProfissionalRemoteFilterColumn): string {
  return `${PROFISSIONAIS_PATHS.LIST}/filtros/${column}`;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

const COLLECTION_KEYS = ["profissionais", "itens", "opcoes", "filtros"];

function toProfissionalPeriodo(raw: unknown): ProfissionalPeriodo | null {
  if (!isRecord(raw)) return null;

  const inicio = readFirstText(raw, "inicio", "dataInicio", "start").trim();
  const fim = readFirstText(raw, "fim", "dataFim", "end").trim();
  if (!inicio && !fim) return null;

  return { inicio, fim };
}

/**
 * RN05/RN06 — a listagem nunca exibe o motivo da indisponibilidade, mesmo que o
 * serviço o informe: por isso nenhum campo de motivo é lido aqui.
 */
function toIndisponibilidade(raw: unknown): ProfissionalIndisponibilidade {
  if (!isRecord(raw)) return { indisponivel: false, periodos: [] };

  const periodos = extractItems(raw.periodos)
    .map(toProfissionalPeriodo)
    .filter((periodo): periodo is ProfissionalPeriodo => periodo !== null);

  const indisponivel =
    typeof raw.indisponivel === "boolean" ? raw.indisponivel : periodos.length > 0;

  return { indisponivel, periodos };
}

function toProfissional(raw: unknown, index: number): Profissional | null {
  if (!isRecord(raw)) return null;

  const nome = readFirstText(raw, "nome", "name").trim();
  const email = readFirstText(raw, "email", "mail").trim();
  if (!nome && !email) return null;

  return {
    id:
      readFirstText(raw, "id", "profissionalId", "usuarioId").trim() || `${nome || email}-${index}`,
    nome,
    email,
    telefone: readFirstText(raw, "telefone", "celular", "phone", "telefoneCelular").trim(),
    cargo: readFirstText(raw, "cargo", "funcao", "role").trim(),
    time: readFirstText(raw, "time", "equipe", "team").trim(),
    fotoUrl: readFirstText(raw, "fotoUrl", "foto_url", "foto", "photoUrl", "avatarUrl").trim(),
    indisponibilidade: toIndisponibilidade(raw.indisponibilidade),
    ...(typeof raw.possuiAlocacaoNoPeriodo === "boolean"
      ? { possuiAlocacaoNoPeriodo: raw.possuiAlocacaoNoPeriodo }
      : {}),
  };
}

/** Descarta valores vazios: um filtro sem valor não restringe a consulta. */
function cleanValues(values: string[] | undefined): string[] {
  return (values ?? []).map((value) => value.trim()).filter((value) => value !== "");
}

/** Busca e filtros de coluna, comuns à listagem e à exportação da visualização. */
function buildCriteriaParams(
  busca: string | undefined,
  filters: ListProfissionaisParams["filters"],
): Record<string, HttpQueryParam> {
  const params: Record<string, HttpQueryParam> = {};

  const term = busca?.trim();
  if (term) params.busca = term;

  // Cargo e Time: multisseleção, enviada como parâmetro repetido (`cargo=A&cargo=B`).
  for (const column of ["cargo", "time"] as const) {
    const values = cleanValues(filters?.[column]);
    if (values.length > 0) params[column] = values;
  }

  // Indisponível: seleção única; só vai na consulta quando há uma escolha.
  const [indisponivel] = cleanValues(filters?.indisponivel);
  if (indisponivel === INDISPONIVEL_FILTER_YES) params.indisponivel = true;
  else if (indisponivel === INDISPONIVEL_FILTER_NO) params.indisponivel = false;

  return params;
}

/** Período Mês/Ano, obrigatório em toda consulta (RN02). */
function buildPeriodoParams(periodo: ProfissionaisPeriodo): Record<string, HttpQueryParam> {
  return {
    periodoInicio: periodo.from,
    periodoFim: periodo.to,
  };
}

/**
 * Consulta somente leitura dos profissionais do Hub (HU 1761): busca, filtros de
 * coluna (`cargo`, `time`, `indisponivel`), o período Mês/Ano obrigatório, paginação 1-based
 * e ordenação no contrato paginado (`page`, `pageSize`, `sort_by`, `sort_direction`).
 */
export async function listProfissionais(
  request: ListProfissionaisParams,
): Promise<ProfissionaisPage> {
  const {
    busca,
    filters,
    periodo,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort = DEFAULT_PROFISSIONAIS_SORT,
    alocacaoId,
    signal,
  } = request;

  const params = { ...buildCriteriaParams(busca, filters), ...buildPeriodoParams(periodo) };
  if (alocacaoId) params.alocacaoId = alocacaoId;

  Object.assign(params, toPaginatedQuery({ page, pageSize, sort }));

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(PROFISSIONAIS_PATHS.LIST, config);

  const profissionais = extractItems(response.data, COLLECTION_KEYS)
    .map(toProfissional)
    .filter((profissional): profissional is Profissional => profissional !== null);

  return { profissionais, total: extractTotal(response.data) ?? profissionais.length };
}

/** Todos os profissionais do período (percorre as páginas), para escolher na equipe da obra. */
export async function listAllProfissionais(
  periodo: ProfissionaisPeriodo,
  signal?: AbortSignal,
): Promise<Profissional[]> {
  return collectAllPages(async (page, pageSize) => {
    const result = await listProfissionais({ periodo, page, pageSize, signal });
    return { items: result.profissionais, total: result.total };
  });
}

/**
 * Exportar Visualização (ponto de entrada do menu ⋮): mesmos critérios da
 * consulta, sem paginação — o arquivo traz o conjunto completo do resultado.
 */
export async function exportProfissionais({
  busca,
  filters,
  filtros,
  periodo,
  sort = DEFAULT_PROFISSIONAIS_SORT,
  format,
  signal,
}: ExportProfissionaisParams): Promise<ExportedFile> {
  const params = { ...buildCriteriaParams(busca, filters), ...buildPeriodoParams(periodo) };

  Object.assign(params, toPaginatedQuery({ sort }));
  params.formato = format;
  appendReportQuery(params, filtros);

  const response = await getHttpClient().get<ArrayBuffer>(PROFISSIONAIS_PATHS.EXPORT, {
    signal,
    params,
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** Comparação de texto no idioma da tela: sem distinguir acento nem caixa. */
const COLLATOR = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

/** `OpcaoFiltroDTO` do contrato: `valor` e `rotulo`. O rótulo cai no valor quando ausente. */
function toFilterOption(raw: unknown): ProfissionalFilterOption | null {
  if (!isRecord(raw)) {
    const value = typeof raw === "string" ? raw.trim() : "";
    return value ? { value, label: value } : null;
  }

  const value = readFirstText(raw, "valor", "value", "id", "codigo").trim();
  if (!value) return null;

  return {
    value,
    label: readFirstText(raw, "rotulo", "label", "descricao", "nome").trim() || value,
  };
}

/**
 * Opções de uma coluna filtrável (Cargo ou Time), já ordenadas pelo rótulo e sem
 * repetição — o serviço é a única origem das opções, independente da página em
 * tela ou do período consultado.
 */
export async function listProfissionalFilterOptions(
  request: ListProfissionalFilterOptionsParams,
): Promise<ProfissionalFilterOption[]> {
  const { column, signal } = request;

  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(
    buildProfissionalFilterOptionsPath(column),
    config,
  );

  const seen = new Set<string>();

  return extractItems(response.data, COLLECTION_KEYS)
    .map(toFilterOption)
    .filter((option): option is ProfissionalFilterOption => {
      if (option === null || seen.has(option.value)) return false;

      seen.add(option.value);
      return true;
    })
    .sort((first, second) => COLLATOR.compare(first.label, second.label));
}
